import { createContext, useContext, useMemo } from 'react';

import {
  TConversionStatusBatchContext,
  TConversionStatusBatchProviderProps,
  TLatestConversionStatusBatchProviderProps,
} from '@/console/features/projects/components/conversion-status-batch/conversion-status-batch-context.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import {
  useReadJobsByProjectIdsQuery,
  useReadLatestJobsByProjectIdsQuery,
} from '@/shared/redux/api/jobs/jobs-api';
import {
  FeatureEnum,
  JobFilterInput,
  JobFragment,
  ReadJobsByProjectIdsQuery,
  ReadLatestJobsByProjectIdsQuery,
} from '@/shared/types/types.generated';

const JOB_CONVERSION_STATUS_POLLING_INTERVAL_MS = 30_000;
const DEFAULT_LIMIT_PER_PROJECT = 25;

const ConversionStatusBatchContext =
  createContext<TConversionStatusBatchContext | null>(null);

export const bucketJobsByVersion = (
  jobs: JobFragment[],
): Map<string, JobFragment[]> => {
  const map = new Map<string, JobFragment[]>();
  for (const item of jobs) {
    for (const vid of item.projectVersionIds ?? []) {
      const existing = map.get(vid);
      if (existing) {
        existing.push(item);
      } else {
        map.set(vid, [item]);
      }
    }
  }
  return map;
};

const useVersionFilter = (versionIds: string[]): JobFilterInput | undefined =>
  useMemo(() => {
    if (versionIds.length === 0) return undefined;
    return {
      projectVersionIds: { containsAny: versionIds },
    };
  }, [versionIds]);

const useConversionStatusContextValue = (
  jobsByVersion: Map<string, JobFragment[]>,
  isLoading: boolean,
): TConversionStatusBatchContext =>
  useMemo(
    () => ({
      getJobsForVersion: (versionId: string) =>
        jobsByVersion.get(versionId) ?? [],
      isLoading,
    }),
    [jobsByVersion, isLoading],
  );

export const ConversionStatusBatchProvider = ({
  projectIds,
  versionIds,
  children,
}: TConversionStatusBatchProviderProps) => {
  const { isJobRunning } = useJobStatus();
  const filter = useVersionFilter(versionIds);

  const { data, isLoading } = usePollingQuery({
    useQueryHook: useReadJobsByProjectIdsQuery,
    queryArg: {
      projectIds,
      jobTypes: [FeatureEnum.ProjectConvert],
      filter,
      pagination: { page: 1, pageSize: versionIds.length * 2 },
    },
    shouldStopPolling: (data: ReadJobsByProjectIdsQuery) =>
      data.readJobsByProjectIds.items.every(
        (item) => !isJobRunning(item.status),
      ),
    pollingInterval: JOB_CONVERSION_STATUS_POLLING_INTERVAL_MS,
    queryOptions: {
      skip: projectIds.length === 0 || versionIds.length === 0,
    },
  });

  const jobsByVersion = useMemo(
    () => bucketJobsByVersion(data?.readJobsByProjectIds?.items ?? []),
    [data],
  );
  const value = useConversionStatusContextValue(jobsByVersion, isLoading);

  return (
    <ConversionStatusBatchContext.Provider value={value}>
      {children}
    </ConversionStatusBatchContext.Provider>
  );
};

export const LatestConversionStatusBatchProvider = ({
  projectIds,
  versionIds,
  limitPerProject = DEFAULT_LIMIT_PER_PROJECT,
  children,
}: TLatestConversionStatusBatchProviderProps) => {
  const { isJobRunning } = useJobStatus();
  const filter = useVersionFilter(versionIds);

  const { data, isLoading } = usePollingQuery({
    useQueryHook: useReadLatestJobsByProjectIdsQuery,
    queryArg: {
      projectIds,
      jobTypes: [FeatureEnum.ProjectConvert],
      filter,
      limitPerProject,
    },
    shouldStopPolling: (data: ReadLatestJobsByProjectIdsQuery) =>
      data.readLatestJobsByProjectIds.every(
        (item) => !isJobRunning(item.status),
      ),
    pollingInterval: JOB_CONVERSION_STATUS_POLLING_INTERVAL_MS,
    queryOptions: {
      skip: projectIds.length === 0 || versionIds.length === 0,
    },
  });

  const jobsByVersion = useMemo(
    () => bucketJobsByVersion(data?.readLatestJobsByProjectIds ?? []),
    [data],
  );
  const value = useConversionStatusContextValue(jobsByVersion, isLoading);

  return (
    <ConversionStatusBatchContext.Provider value={value}>
      {children}
    </ConversionStatusBatchContext.Provider>
  );
};

export const useConversionStatusBatch = () =>
  useContext(ConversionStatusBatchContext);
